export function tbToChartData(
  data: Record<string, { ts: number; value: string }[]>,
  key: string,
  range: string,
  fallback: any[],
  conversionFactor: number = 1
) {
  if (range === 'realtime') {
    if (!data || !data[key] || data[key].length === 0) return fallback;
    const arr = [...data[key]].sort((a, b) => a.ts - b.ts);
    return arr.map(d => {
      const date = new Date(d.ts);
      return {
        name: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        ts: d.ts,
        value: Number(((parseFloat(d.value) || 0) * conversionFactor).toFixed(2))
      };
    });
  }

  const timeMap = new Map<string, any>();
  const now = new Date();
  
  if (range === 'daily') {
    now.setMinutes(0, 0, 0);
    for (let i = 23; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 3600000);
      const name = `${d.getHours()}시`;
      timeMap.set(name, { name, value: 0, ts: d.getTime() });
    }
  } else if (range === 'weekly' || range === 'monthly') {
    now.setHours(0, 0, 0, 0);
    const count = range === 'weekly' ? 7 : 30;
    for (let i = count - 1; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 86400000);
      const name = range === 'weekly' 
        ? ['일', '월', '화', '수', '목', '금', '토'][d.getDay()]
        : `${d.getDate()}일`;
      timeMap.set(name, { name, value: 0, ts: d.getTime() });
    }
  } else {
    return fallback;
  }

  if (data && data[key]) {
    for (const d of data[key]) {
      const date = new Date(d.ts);
      let name = '';
      if (range === 'daily') name = `${date.getHours()}시`;
      else if (range === 'weekly') name = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
      else if (range === 'monthly') name = `${date.getDate()}일`;
      
      if (timeMap.has(name)) {
        timeMap.get(name).value = Number(((parseFloat(d.value) || 0) * conversionFactor).toFixed(2));
      }
    }
  }

  return Array.from(timeMap.values());
}

export function tbToCombinedChartData(
  data: Record<string, { ts: number; value: string }[]>,
  key1: string,
  key1OutputKey: string,
  key2: string,
  key2OutputKey: string,
  range: string,
  fallback: any[],
  conversionFactor1: number = 1,
  conversionFactor2: number = 1
) {
  if (range === 'realtime') {
    if (!data || (!data[key1] && !data[key2])) return fallback;
    const timeMap = new Map<number, any>();
    const processRealtime = (arr: { ts: number; value: string }[] | undefined, outKey: string, factor: number) => {
      if (!arr) return;
      const sorted = [...arr].sort((a, b) => a.ts - b.ts);
      for (const d of sorted) {
        // Group points into 5-second buckets so simultaneous key reports pair together seamlessly
        const bucketTs = Math.round(d.ts / 5000) * 5000;
        const date = new Date(bucketTs);
        const name = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        if (!timeMap.has(bucketTs)) {
          timeMap.set(bucketTs, {
            name,
            ts: bucketTs,
            [key1OutputKey]: null,
            [key2OutputKey]: null
          });
        }
        const entry = timeMap.get(bucketTs);
        entry[outKey] = Number(((parseFloat(d.value) || 0) * factor).toFixed(2));
      }
    };
    processRealtime(data[key1], key1OutputKey, conversionFactor1);
    processRealtime(data[key2], key2OutputKey, conversionFactor2);
    
    if (timeMap.size === 0) return fallback;
    const result = Array.from(timeMap.values()).sort((a, b) => a.ts - b.ts);
    
    let last1: number | null = null;
    let last2: number | null = null;
    for (const r of result) {
      if (r[key1OutputKey] !== null) last1 = r[key1OutputKey]; else r[key1OutputKey] = last1;
      if (r[key2OutputKey] !== null) last2 = r[key2OutputKey]; else r[key2OutputKey] = last2;
    }
    return result;
  }

  const timeMap = new Map<string, any>();
  const now = new Date();
  
  if (range === 'daily') {
    now.setMinutes(0, 0, 0);
    for (let i = 23; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 3600000);
      const name = `${d.getHours()}시`;
      timeMap.set(name, { name, ts: d.getTime(), [key1OutputKey]: null, [key2OutputKey]: null });
    }
  } else if (range === 'weekly' || range === 'monthly') {
    now.setHours(0, 0, 0, 0);
    const count = range === 'weekly' ? 7 : 30;
    for (let i = count - 1; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 86400000);
      const name = range === 'weekly' 
        ? ['일', '월', '화', '수', '목', '금', '토'][d.getDay()]
        : `${d.getDate()}일`;
      timeMap.set(name, { name, ts: d.getTime(), [key1OutputKey]: null, [key2OutputKey]: null });
    }
  } else {
    return fallback;
  }

  const processArray = (arr: { ts: number; value: string }[] | undefined, outKey: string, factor: number) => {
    if (!arr) return;
    for (const d of arr) {
      const date = new Date(d.ts);
      let name = '';
      if (range === 'daily') name = `${date.getHours()}시`;
      else if (range === 'weekly') name = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
      else if (range === 'monthly') name = `${date.getDate()}일`;
      
      if (timeMap.has(name)) {
        timeMap.get(name)[outKey] = Number(((parseFloat(d.value) || 0) * factor).toFixed(2));
      }
    }
  };

  processArray(data?.[key1], key1OutputKey, conversionFactor1);
  processArray(data?.[key2], key2OutputKey, conversionFactor2);

  const result = Array.from(timeMap.values());
  
  let last1: number | null = null;
  let last2: number | null = null;
  for (const r of result) {
    if (r[key1OutputKey] !== null) last1 = r[key1OutputKey]; else r[key1OutputKey] = last1;
    if (r[key2OutputKey] !== null) last2 = r[key2OutputKey]; else r[key2OutputKey] = last2;
  }

  return result;
}

export function tbToErvCo2ChartData(
  ervData: Record<string, { ts: number; value: any }[]> | undefined,
  co2Data: Record<string, { ts: number; value: any }[]> | undefined,
  range: string,
  fallback: any[] = []
) {
  const ervRaw = (ervData?.['erv'] || []).slice().sort((a, b) => a.ts - b.ts);
  const co2Raw = (co2Data?.['CO2'] || []).slice().sort((a, b) => a.ts - b.ts);

  if (ervRaw.length === 0 && co2Raw.length === 0) {
    return fallback;
  }

  const now = Date.now();
  let windowMs = 24 * 60 * 60 * 1000;
  if (range === 'realtime') windowMs = 30 * 60 * 1000;
  else if (range === 'weekly') windowMs = 7 * 24 * 60 * 60 * 1000;
  else if (range === 'monthly') windowMs = 30 * 24 * 60 * 60 * 1000;

  const startTs = now - windowMs;
  const endTs = now;

  // 1. Collect all exact raw event timestamps from both ERV and CO2 (no summarization)
  const allTs = new Set<number>();
  allTs.add(startTs);
  allTs.add(endTs);

  ervRaw.forEach(d => {
    if (d.ts >= startTs && d.ts <= endTs) allTs.add(d.ts);
  });
  co2Raw.forEach(d => {
    if (d.ts >= startTs && d.ts <= endTs) allTs.add(d.ts);
  });

  const sortedTs = Array.from(allTs).sort((a, b) => a - b);

  // Exact raw maps
  const ervMap = new Map<number, number>();
  ervRaw.forEach(d => {
    let v = Math.round(Number(d.value));
    if (isNaN(v) || v < 0) v = 0;
    ervMap.set(d.ts, Math.max(0, Math.min(3, v)));
  });

  const co2Map = new Map<number, number>();
  co2Raw.forEach(d => {
    const v = parseFloat(d.value);
    if (!isNaN(v) && v > 0) co2Map.set(d.ts, Math.round(v));
  });

  // Find initial state before startTs
  let runningErv = 0;
  const priorErv = ervRaw.filter(d => d.ts <= startTs);
  if (priorErv.length > 0) {
    const pv = Math.round(Number(priorErv[priorErv.length - 1].value));
    if (!isNaN(pv) && pv >= 0) runningErv = Math.max(0, Math.min(3, pv));
  } else if (ervRaw.length > 0) {
    const fv = Math.round(Number(ervRaw[0].value));
    if (!isNaN(fv) && fv >= 0) runningErv = Math.max(0, Math.min(3, fv));
  }

  return sortedTs.map(ts => {
    if (ervMap.has(ts)) runningErv = ervMap.get(ts)!;
    const co2Val = co2Map.has(ts) ? co2Map.get(ts)! : null;

    const d = new Date(ts);
    let name = '';
    if (range === 'realtime') {
      name = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    } else if (range === 'daily') {
      name = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
    } else if (range === 'weekly') {
      name = `${d.getMonth() + 1}/${d.getDate()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
    } else {
      name = `${d.getMonth() + 1}/${d.getDate()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
    }

    return {
      name,
      ts,
      erv: runningErv,
      co2: co2Val
    };
  });
}


